import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyBox, Pagination, QueryBoundary, Table, Td, Th } from '../components/ui';
import { dateOnly, money, num, pct } from '../lib/format';

const LIMIT = 50;

export default function Backtests() {
  const [offset, setOffset] = useState(0);
  const q = useQuery({
    queryKey: ['backtests', offset],
    queryFn: () => api.backtests({ limit: LIMIT, offset }),
  });

  return (
    <>
      <PageHeader
        title="Backtests"
        description="Stored runs. Keyed by (strategy_version, config_hash, dataset_hash, seed) — re-running the same inputs replaces the row (INV-8)."
      />
      <QueryBoundary
        q={q}
        isEmpty={q.data?.items.length === 0}
        empty={
          <EmptyBox>
            No backtests saved yet. Run one from{' '}
            <Link className="text-sky-400 hover:underline" to="/operations">
              Operations
            </Link>
            .
          </EmptyBox>
        }
      >
        {q.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Run</Th>
                  <Th>Strategy</Th>
                  <Th>Symbol / TF</Th>
                  <Th>Range</Th>
                  <Th className="text-right">Start → End</Th>
                  <Th className="text-right">Return</Th>
                  <Th className="text-right">Ambiguous</Th>
                  <Th>Seed</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((b) => {
                  const start = Number(b.initial_balance);
                  const end = Number(b.final_balance);
                  const ret = start > 0 ? (end - start) / start : 0;
                  return (
                    <tr key={b.id} className="hover:bg-slate-800/30">
                      <Td>
                        <Link className="text-sky-400 hover:underline" to={`/backtests/${b.id}`}>
                          #{b.id}
                        </Link>
                      </Td>
                      <Td className="text-xs">
                        {b.strategy_id}
                        <span className="ml-1 text-slate-500">{b.strategy_version}</span>
                      </Td>
                      <Td>
                        {b.symbol} · {b.timeframe}
                      </Td>
                      <Td className="whitespace-nowrap text-xs text-slate-400">
                        {dateOnly(b.from_utc)} → {dateOnly(b.to_utc)}
                      </Td>
                      <Td className="text-right tabular-nums text-xs">
                        {money(b.initial_balance)} → {money(b.final_balance)}
                      </Td>
                      <Td
                        className={`text-right tabular-nums ${ret < 0 ? 'text-red-400' : ret > 0 ? 'text-emerald-400' : ''}`}
                      >
                        {pct(ret, 1)}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {b.ambiguous_share > 0.02 ? (
                          <Badge tone="warn">{pct(b.ambiguous_share, 1)}</Badge>
                        ) : (
                          pct(b.ambiguous_share, 1)
                        )}
                      </Td>
                      <Td className="tabular-nums text-xs text-slate-500">{num(b.seed, 0)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination total={q.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>
    </>
  );
}
